import React, { useId, useState } from 'react';
import { ChevronDown, Pencil, Trash2 } from 'lucide-react';
import Rating from './Rating';

const formatDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

// A review as an editorial entry: verdict on the left, words on the right.
// The excerpt is the lead; the full text opens on request instead of
// repeating the excerpt underneath it.
const ReviewCard = ({ review, isAdmin, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const contentId = useId();
  const date = formatDate(review.date);
  const paragraphs = String(review.content || '')
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <article className="grid gap-6 py-10 md:grid-cols-12 md:gap-8">
      <div className="space-y-3 md:col-span-3">
        <Rating value={review.rating} />
        {date && (
          <time dateTime={review.date} className="block font-mono text-xs text-slate-400">
            {date}
          </time>
        )}
        {isAdmin && (
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={() => onEdit(review)} className="icon-btn" title="Edit Review" aria-label="Edit review">
              <Pencil size={16} strokeWidth={1.75} />
            </button>
            <button type="button" onClick={() => onDelete(review.id)} className="icon-btn-danger" title="Delete Review" aria-label="Delete review">
              <Trash2 size={16} strokeWidth={1.75} />
            </button>
          </div>
        )}
      </div>

      <div className="min-w-0 md:col-span-9">
        <h2 className="break-words text-2xl font-semibold tracking-tight text-slate-50 md:text-3xl">
          {review.title}
        </h2>
        {review.excerpt && (
          <p className="mt-4 max-w-[65ch] text-lg leading-relaxed text-slate-200">{review.excerpt}</p>
        )}

        {paragraphs.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              aria-controls={contentId}
              className="text-link mt-5 text-sm"
            >
              {open ? 'Hide full review' : 'Read full review'}
              <ChevronDown
                size={16}
                strokeWidth={1.75}
                className={`transition-transform duration-200 ease-out ${open ? 'rotate-180' : ''}`}
              />
            </button>
            <div id={contentId} hidden={!open} className="mt-5 max-w-[65ch] space-y-4 leading-relaxed text-slate-300">
              {paragraphs.map((p, i) => (
                <p key={i} className="break-words">{p}</p>
              ))}
            </div>
          </>
        )}
      </div>
    </article>
  );
};

export default ReviewCard;
